import { isEmpty, isObject, transform } from 'lodash';
import { allErrorMessages } from '~/constants/errorMessages.ts';
import { TMultipartHeaders, TMultipartResponse, TParsedBase64File } from '~/helpers/types.ts';
import { CustomResolveOptions } from '~/modules/identification/types';

const getHeaderValue = (headers: TMultipartHeaders, key: string): string | undefined => {
  if (headers instanceof Headers) {
    return headers.get(key) ?? undefined;
  }

  const value = headers[key];

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return value == null ? undefined : String(value);
};

export const formatDateReverse = (dateValue: string | Date | undefined): string | undefined => {
  if (!dateValue) return undefined;

  let dateString: string;

  if (dateValue instanceof Date) {
    const day = String(dateValue.getDate()).padStart(2, '0');
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const year = dateValue.getFullYear();
    dateString = `${day}.${month}.${year}`;
  } else {
    dateString = dateValue;
  }

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('.');
    return `${year}-${month}-${day}`;
  }

  return dateString;
};

export const parseDate = (dateString: string) => {
  if (!dateString) return null;
  const [day, month, year] = dateString.split('.').map(Number);
  const date = new Date(year || -1, (month || 0) - 1, day);
  return !isNaN(date.getTime()) ? date : null;
};

export const formatDateDDMMYYYY = (dateString: string | Date) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const formatDateForDisplay = (dateValue: string | Date | null | undefined): string => {
  if (!dateValue) return '';
  if (dateValue instanceof Date) {
    return formatDateDDMMYYYY(dateValue);
  }
  if (isValidDateFormat(dateValue)) {
    return dateValue;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split('-');
    return `${day}.${month}.${year}`;
  }
  return formatDateDDMMYYYY(dateValue);
};

export const formatDateToISO = (
  dateInput: string | Date | null | undefined,
  timeType: 'start' | 'end' = 'start'
): string => {
  if (!dateInput) return '';

  let day: number;
  let month: number;
  let year: number;

  if (typeof dateInput === 'string' && dateInput.includes('.')) {
    const parts = dateInput.split('.');
    if (parts.length !== 3) return '';

    day = parseInt(parts[0] as string, 10);
    month = parseInt(parts[1] as string, 10) - 1;
    year = parseInt(parts[2] as string, 10);
  } else if (dateInput instanceof Date) {
    day = dateInput.getDate();
    month = dateInput.getMonth();
    year = dateInput.getFullYear();
  } else if (typeof dateInput === 'string' && dateInput.includes('-')) {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';

    day = date.getDate();
    month = date.getMonth();
    year = date.getFullYear();
  } else {
    return '';
  }

  if (isNaN(day) || isNaN(month) || isNaN(year)) return '';
  if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1000) return '';

  const date =
    timeType === 'start'
      ? new Date(year, month, day, 0, 0, 0, 0)
      : new Date(year, month, day, 23, 59, 59, 999);

  if (isNaN(date.getTime())) return '';
  return date.toISOString();
};

export const isValidDateFormat = (value: string) => {
  const regex = /^\d{2}\.\d{2}\.\d{4}$/;
  return regex.test(value);
};

export const newFormattedDate = (date: string | Date | null): Date | null => {
  return typeof date === 'string'
    ? isValidDateFormat(date)
      ? parseDate(date)
      : new Date(date)
    : date;
};

export const getErrorKey = (errorText: string) =>
  Object.keys(allErrorMessages).find(
    (item) => allErrorMessages[item as keyof typeof allErrorMessages] === errorText
  ) || '';

export const normalizeCssSize = (size: number | string | undefined, fallback: string): string => {
  if (typeof size === 'number') {
    return `${size}px`;
  }

  return size ?? fallback;
};

export function cleanEmpty<T>(obj: T): T {
  if (!isObject(obj)) return obj;

  const accumulator = (Array.isArray(obj) ? [] : {}) as Record<string, unknown>;

  return transform<Record<string, unknown>, Record<string, unknown>>(
    obj as Record<string, unknown>,
    (result, value, key) => {
      const cleaned = isObject(value) ? cleanEmpty(value) : value;

      if (!isObject(cleaned) || !isEmpty(cleaned)) {
        result[key] = cleaned;
      }
    },
    accumulator
  ) as T;
}

export const convertFileToBase64 = (file: File): Promise<TParsedBase64File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve({
        base64File: reader.result,
        name: file.name,
        size: file.size,
      });
    };

    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file);
  });
};

export const parseMultipartData = async (response: TMultipartResponse, toBase64 = false) => {
  const contentType = getHeaderValue(response.headers, 'content-type');

  const nativeResponse = new Response(response.data, {
    headers: {
      'content-type': contentType ?? '',
    },
    status: response.status,
    statusText: response.statusText,
  });
  const formData = await nativeResponse.formData();

  const filesParsed: File[] = [];
  for (const [, value] of formData.entries()) {
    if (value instanceof File) {
      filesParsed.push(value);
    }
  }

  let base64Files: TParsedBase64File[] = [];
  if (toBase64) {
    base64Files = await Promise.all(filesParsed.map(convertFileToBase64));
  }

  return {
    files: filesParsed,
    names: filesParsed.map((file) => file.name),
    base64Files,
  };
};

export const formatTime = (timeLeft: number): string => {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = Math.floor(timeLeft % 60);
  return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const isFeatureOn = (key: string) => (import.meta.env[key] || '').toLowerCase() === 'on';

export const onInvalidForm = (errors: unknown) => console.log(errors);

export function getValidationFormValue<T>(options: CustomResolveOptions, index: number): T {
  return (options as CustomResolveOptions)?.from?.[index]?.value as T;
}

export const roundNumber = (value?: number) => Math.round((value ?? 0) * 1_000_000) / 1_000_000;

export const withBlobContentType = (blob: Blob, contentType?: string | null): Blob =>
  !blob.type && contentType ? blob.slice(0, blob.size, contentType) : blob;

export const isEmptyValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};
